import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@paguz/ui';
import { ShieldAlertIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { describeError } from '../api/client.ts';
import { Field, Textarea, compactInputClass } from './form-controls.tsx';

export const CONFIRM_WORD = 'CONFIRMAR';
export const MIN_REASON_LENGTH = 5;

export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Resumen de lo que va a pasar (filas etiqueta/valor o contenido libre). */
  summary?: ReactNode;
  /** Palabra que hay que escribir (por defecto `CONFIRMAR`; puede ser el ID). */
  confirmWord?: string;
  /** Pide motivo obligatorio (queda en auditoría). */
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  confirmLabel: string;
  destructive?: boolean;
  /** Campos extra (p. ej. motivo codificado) antes del motivo libre. */
  extra?: ReactNode;
  /** Deshabilita la confirmación mientras los campos extra no sean válidos. */
  extraValid?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
}

/**
 * Doble confirmación (`06-backoffice.md` §2): modal con resumen + escribir la palabra de
 * confirmación + motivo obligatorio. El error de la API se muestra dentro del modal.
 */
export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  summary,
  confirmWord = CONFIRM_WORD,
  requireReason = true,
  reasonLabel = 'Motivo (queda en auditoría)',
  reasonPlaceholder = 'Explica por qué haces esta acción…',
  confirmLabel,
  destructive = false,
  extra,
  extraValid = true,
  onConfirm,
}: ConfirmActionDialogProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wordId = useId();
  const reasonId = useId();

  const wordOk = typed.trim() === confirmWord;
  const reasonOk = !requireReason || reason.trim().length >= MIN_REASON_LENGTH;
  const canConfirm = wordOk && reasonOk && extraValid && !pending;

  function close(next: boolean) {
    if (pending) return;
    if (!next) {
      setTyped('');
      setReason('');
      setError(null);
    }
    onOpenChange(next);
  }

  async function submit() {
    if (!canConfirm) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      setTyped('');
      setReason('');
      onOpenChange(false);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div
            className={cn(
              'mb-1 flex size-10 items-center justify-center rounded-full',
              destructive
                ? 'bg-danger-soft text-danger-soft-foreground'
                : 'bg-highlight-soft text-highlight-soft-foreground',
            )}
            aria-hidden="true"
          >
            <ShieldAlertIcon className="size-5" />
          </div>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {summary && (
            <div className="rounded-xl border border-border bg-muted/60 p-4 text-sm">{summary}</div>
          )}
          {extra}
          {requireReason && (
            <Field
              label={reasonLabel}
              htmlFor={reasonId}
              hint={`Mínimo ${MIN_REASON_LENGTH} caracteres.`}
            >
              <Textarea
                id={reasonId}
                value={reason}
                placeholder={reasonPlaceholder}
                maxLength={1000}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
            </Field>
          )}
          <Field
            label={
              <>
                Escribe <code className="rounded bg-muted px-1 font-mono">{confirmWord}</code> para
                continuar
              </>
            }
            htmlFor={wordId}
          >
            <Input
              id={wordId}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              className={cn(compactInputClass, 'font-mono')}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
            />
          </Field>
          {error && (
            <p
              role="alert"
              className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-foreground"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                close(false);
              }}
              disabled={pending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={destructive ? 'destructive' : 'highlight'}
              disabled={!canConfirm}
              loading={pending}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Filas del resumen del modal. */
export function SummaryRows({ rows }: { rows: readonly [label: ReactNode, value: ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-4 gap-y-1.5">
      {rows.map(([label, value], index) => (
        <div key={index} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 font-medium break-words text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
